/**
 * TrainerSessionPage — «Речевая беглость» (протокол MoCA, задание на беглость).
 *
 * Пациент ГОВОРИТ слова вслух в течение 60 секунд — никакой печати.
 *
 * Сценарий (как при живом протоколировании MoCA — «инструкцию читает голос»):
 *   1. СТАРТ БЕЗ ВТОРОГО КАСАНИЯ: клик «Тренироваться» в кабинете уже дал
 *      браузеру пользовательскую активацию (переход на /trainer — клиентский,
 *      внутри той же вкладки), поэтому страница СРАЗУ предзапрашивает микрофон
 *      (диалог разрешения не съедает время задания) и играет ГОЛОСОВУЮ
 *      ИНСТРУКЦИЮ (аудио-ссылка приходит с сервера в
 *      GET /api/trainers/session/{stepId}: config.fluency.audioUrl, или
 *      audio_url на любом уровне config/сессии — файл записывает команда,
 *      ссылка кладётся в колонку audio_url таблицы trainer_bank).
 *      ВАЖНО: ссылка на /trainer должна быть <Link>/navigate (та же вкладка),
 *      НЕ target="_blank" и не полное обновление страницы — иначе активация
 *      теряется и показывается фолбэк (п.3).
 *   2. Аудио закончилось («…Время пошло») → ТАЙМЕР 60 СЕК СТАРТУЕТ САМ,
 *      без всякой кнопки. Если аудио ещё не записано/не отдаётся сервером —
 *      короткий отсчёт «3-2-1» на кольце и автостарт, чтобы задание
 *      запускалось одинаково всегда.
 *   3. ФОЛБЭК «Коснитесь экрана» — ТОЛЬКО если /trainer открыт напрямую
 *      (обновление страницы, закладка, открытие в новой вкладке): без
 *      пользовательской активации браузер блокирует автоплей и/или микрофон.
 *      После касания сценарий тот же: микрофон → инструкция → таймер.
 *   4. Идёт Web Speech API (src/services/speechRecognition.js, ru-RU, continuous);
 *      Chrome сам останавливается на паузах — перезапускаем в onend, пока идёт минута.
 *   5. Время вышло → transcript уходит на POST /api/trainers — СЧЁТ СЧИТАЕТ
 *      СЕРВЕР (api/app/scoring/fluency.py: повторы, слова не по заданию и
 *      однокоренные не засчитываются). Экран результата: count + counted.
 *
 * ASR сейчас — браузерный (Web Speech API). При переносе на собственный движок
 * (Vox) заменяется только источник transcript — контракт POST /api/trainers
 * не меняется, страница останется как есть.
 *
 * Маршрут: /trainer — пациент сам; /trainer?patientId=UUID — запуск врачом из
 * карточки пациента (день считается по истории этого пациента). Дни: первое
 * прохождение — день 1, далее календарные дни от первой записи;
 * stepId = ceil(day/2) (40 дней программы → 20 блоков методички).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  ArrowLeft,
  BrainCircuit,
  CheckCircle2,
  Loader2,
  Mic,
  RotateCcw,
  Volume2,
} from 'lucide-react';
import {
  getAuthUserId,
  getTrainerSession,
  listTrainerReports,
  localToday,
  saveTrainerReport,
} from '../lib/cortivoxApi';
import { createRecognition, isRecognitionSupported } from '../services/speechRecognition';

const PROGRAM_DAYS = 40;
const DURATION_MS = 60_000; // 60 секунд, как в MoCA
const MAX_WORDS = 100; // защита от аномально длинного transcript
const AUDIO_SAFETY_MS = 240_000; // если аудио «зависло» — стартуем принудительно
const RING_R = 54;
const RING_C = 2 * Math.PI * RING_R;

// Человекочитаемые подписи категорий (ключи category из cortivox.trainer_bank).
// Основные — animals_domestic и animals_wild (как договорились). Ключ "animals"
// — старая общая категория из необновлённого сида: показываем «Животные»,
// пока в таблице не проставлены animals_domestic/animals_wild.
// RU-версия; когда появится EN-интерфейс — добавляется словарь CATEGORY_LABELS_EN
// и выбор словаря по языку интерфейса, структура страницы не меняется.
const CATEGORY_LABELS_RU = {
  animals_domestic: 'Домашние животные',
  animals_wild: 'Дикие животные',
  animals: 'Животные',
  plants: 'Растения',
  furniture: 'Мебель',
  dishes: 'Посуда',
  birds: 'Птицы',
  cities: 'Города',
  professions: 'Профессии',
  sea_creatures: 'Морские обитатели',
  weather: 'Погода',
  kitchen_utensils: 'Кухонная утварь',
  vegetables: 'Овощи',
  trees: 'Деревья',
  flowers: 'Цветы',
  fruits: 'Фрукты',
  instruments: 'Музыкальные инструменты',
  fish: 'Рыбы',
  verbs: 'Глаголы',
};
const CATEGORY_LABELS = CATEGORY_LABELS_RU;

const stepIdForDay = (day) =>
  String(Math.ceil(Math.min(PROGRAM_DAYS, Math.max(1, Number(day) || 1)) / 2)).padStart(2, '0');

const dayToUtc = (iso) => {
  const [y, m, d] = String(iso).split('-').map(Number);
  return Date.UTC(y || 1970, (m || 1) - 1, d || 1);
};

// Первое прохождение = день 1, дальше — календарные дни от первой записи.
const resolveDayFromReports = (items, todayIso) => {
  const dates = (Array.isArray(items) ? items : [])
    .map((it) => String(it?.scoredOn || '').slice(0, 10))
    .filter((s) => /^\d{4}-\d{2}-\d{2}$/.test(s))
    .sort();
  if (dates.length === 0) return 1;
  const first = dates[0];
  if (todayIso <= first) return 1;
  const diffDays = Math.floor((dayToUtc(todayIso) - dayToUtc(first)) / 86_400_000);
  return Math.min(PROGRAM_DAYS, Math.max(1, diffDays + 1));
};

const describeFluency = (fluency) => {
  if (!fluency) return null;
  if (fluency.mode === 'phonemic' && fluency.letter) {
    return `Буква «${String(fluency.letter).toUpperCase()}»`;
  }
  if (fluency.mode === 'semantic' && fluency.category) {
    const key = String(fluency.category);
    return CATEGORY_LABELS[key] || key.replace(/_/g, ' ');
  }
  if (fluency.mode === 'pos') return 'Глаголы';
  return null;
};

// Ссылка на голосовую инструкцию — где её ни положил бэкенд, найдём.
const pickAudioUrl = (data) => {
  if (!data) return '';
  const c = data.config || {};
  const f = c.fluency || {};
  const candidates = [
    f.audioUrl,
    f.audio_url,
    f.audioLink,
    f.audio_link,
    c.audioUrl,
    c.audio_url,
    c.audioLink,
    c.audio_link,
    data.audioUrl,
    data.audio_url,
    data.audioLink,
    data.audio_link,
    data.questionAudio,
    data.question_audio,
  ];
  const found = candidates.find((v) => typeof v === 'string' && v.trim().length > 0);
  return found ? found.trim() : '';
};

const pluralWords = (n) => {
  const a = Math.abs(n) % 100;
  const b = a % 10;
  if (a > 10 && a < 20) return 'слов';
  if (b > 1 && b < 5) return 'слова';
  if (b === 1) return 'слово';
  return 'слов';
};

const extractItems = (list) => {
  if (Array.isArray(list)) return list;
  return Array.isArray(list?.items) ? list.items : [];
};

const hintFromError = (err) => {
  const status = err?.status ?? err?.response?.status;
  if (status === 403 || status === 404) {
    return 'Если вы врач: открывайте тренажёр из карточки пациента — /trainer?patientId=UUID пациента.';
  }
  return null;
};

export default function TrainerSessionPage() {
  // phases: loading | intro | priming | running | saving | done | error
  const [phase, setPhase] = useState('loading');
  const [session, setSession] = useState(null);
  const [result, setResult] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [errorHint, setErrorHint] = useState('');
  const [patientId, setPatientId] = useState(null);
  const [stepId, setStepId] = useState(null);
  const [remainingMs, setRemainingMs] = useState(DURATION_MS);
  const [liveText, setLiveText] = useState('');
  const [countdown, setCountdown] = useState(0); // отсчёт 3-2-1, когда аудио нет

  // --- Web Speech API -------------------------------------------------------
  const recognitionRef = useRef(null);
  const runningRef = useRef(false); // true, пока задание идёт (для перезапуска onend)
  const finalsRef = useRef([]); // финальные сегменты распознавания
  const interimRef = useRef(''); // текущий (нефинальный) сегмент

  // --- голосовая инструкция -------------------------------------------------
  const audioRef = useRef(null);
  const audioSafetyRef = useRef(0);

  // Автостарт должен случиться ровно один раз на загрузку страницы.
  const autoStartedRef = useRef(false);
  // «Пропустить инструкцию» нажата — автозапуск аудио отменяется.
  const skippedRef = useRef(false);

  const supported = useMemo(() => isRecognitionSupported(), []);

  const audioUrl = useMemo(() => pickAudioUrl(session), [session]);

  const durationMs = useMemo(() => {
    const raw = session?.config?.showTimer;
    // showTimer — число секунд длительности (bool/undefined → 60 секунд MoCA)
    const secs = typeof raw === 'number' && raw > 0 ? raw : null;
    return secs ? secs * 1000 : DURATION_MS;
  }, [session]);

  const stopAudio = useCallback(() => {
    window.clearTimeout(audioSafetyRef.current);
    audioSafetyRef.current = 0;
    const a = audioRef.current;
    audioRef.current = null;
    if (!a) return;
    try {
      a.onended = null;
      a.onerror = null;
      a.pause();
      a.src = '';
    } catch {
      /* ignore */
    }
  }, []);

  // Разбор финального transcript + отправка на сервер (счёт считает сервер).
  const finishRun = useCallback(async () => {
    runningRef.current = false;
    try {
      recognitionRef.current?.stop();
    } catch {
      /* уже остановлен — не страшно */
    }
    recognitionRef.current = null;

    const raw = [...finalsRef.current, interimRef.current].filter(Boolean).join(' ');
    const words = raw.replace(/\s+/g, ' ').trim().split(' ').filter(Boolean).slice(0, MAX_WORDS);
    const transcript = words.join(' ');

    setPhase('saving');
    try {
      const res = await saveTrainerReport({ patientId, stepId, transcript, today: localToday() });
      setResult(res);
      setPhase('done');
    } catch (err) {
      setErrorMsg(err?.message || 'Не удалось сохранить результат.');
      setErrorHint(hintFromError(err) || '');
      setPhase('error');
    }
  }, [patientId, stepId]);

  const finishRef = useRef(finishRun);
  useEffect(() => {
    finishRef.current = finishRun;
  }, [finishRun]);

  // Таймер задания: стартует вместе с фазой running, по нулю — finishRun.
  const finishedRef = useRef(false);
  useEffect(() => {
    if (phase !== 'running') return undefined;
    finishedRef.current = false;
    const startedAt = Date.now();
    const tick = () => {
      const left = Math.max(0, durationMs - (Date.now() - startedAt));
      setRemainingMs(left);
      if (left <= 0 && !finishedRef.current) {
        finishedRef.current = true;
        finishRef.current();
      }
    };
    tick();
    const timer = window.setInterval(tick, 200);
    return () => window.clearInterval(timer);
  }, [phase, durationMs]);

  // Предупреждение при случайном закрытии вкладки посреди задания.
  useEffect(() => {
    if (phase !== 'running') return undefined;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [phase]);

  // Остановить распознавание и аудио при размонтировании страницы.
  useEffect(
    () => () => {
      runningRef.current = false;
      try {
        recognitionRef.current?.stop();
      } catch {
        /* ignore */
      }
      stopAudio();
    },
    [stopAudio],
  );

  const load = useCallback(async () => {
    setPhase('loading');
    setErrorMsg('');
    setErrorHint('');
    setSession(null);
    setResult(null);
    setLiveText('');
    setRemainingMs(DURATION_MS);
    setCountdown(0);
    stopAudio();
    try {
      const params = new URLSearchParams(window.location.search);
      const pidOverride = params.get('patientId');
      const dayOverride = Number(params.get('day')) || null;

      const uid = pidOverride || (await getAuthUserId());
      if (!uid) {
        setErrorMsg('Не удалось определить пользователя. Войдите в аккаунт и откройте тренажёр заново.');
        setPhase('error');
        return;
      }

      const list = pidOverride
        ? await listTrainerReports({ patientId: pidOverride, limit: 200 })
        : await listTrainerReports({ limit: 200 });

      const day = dayOverride || resolveDayFromReports(extractItems(list), localToday());
      const sid = stepIdForDay(day);
      const data = await getTrainerSession(sid);

      setPatientId(uid);
      setStepId(sid);
      setSession(data);
      // Старт СРАЗУ, без экрана «Коснитесь экрана» (сценарий, п.1).
      // intro остаётся фолбэком для прямого открытия /trainer (п.3 сценария):
      // без пользовательской активации браузер блокирует звук/микрофон.
      autoStartedRef.current = false;
      skippedRef.current = false;
      setPhase(supported ? 'priming' : 'intro');
    } catch (err) {
      setErrorMsg(err?.message || 'Не удалось загрузить задание.');
      setErrorHint(hintFromError(err) || '');
      setPhase('error');
    }
  }, [stopAudio, supported]);

  useEffect(() => {
    void load();
  }, [load]);

  // СТАРТ ЗАДАНИЯ: микрофон → распознавание → фаза running (таймер стартует эффектом).
  // Вызывается ПОСЛЕ конца голосовой инструкции (или после отсчёта, если аудио нет).
  const startRun = useCallback(async () => {
    stopAudio();
    setCountdown(0);

    // Микрофон уже разрешён касанием — но перепроверим (не съедая времени):
    try {
      if (navigator.mediaDevices?.getUserMedia) {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach((t) => t.stop());
      }
    } catch {
      runningRef.current = false;
      setErrorMsg('Нет доступа к микрофону. Разрешите доступ к микрофону в настройках браузера и попробуйте ещё раз.');
      setPhase('error');
      return;
    }

    const rec = createRecognition({ lang: 'ru-RU', continuous: true, interimResults: true });
    if (!rec) {
      setErrorMsg('Браузер не поддерживает распознавание речи. Откройте тренажёр в Google Chrome или Яндекс.Браузере.');
      setPhase('error');
      return;
    }

    finalsRef.current = [];
    interimRef.current = '';
    setLiveText('');
    runningRef.current = true;
    recognitionRef.current = rec;

    rec.onresult = (event) => {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const res = event.results[i];
        const text = String(res?.[0]?.transcript || '').trim();
        if (!text) continue;
        if (res.isFinal) finalsRef.current.push(text);
        else interim += `${text} `;
      }
      interimRef.current = interim.trim();
      setLiveText([...finalsRef.current, interimRef.current].filter(Boolean).join(' '));
    };

    rec.onerror = (event) => {
      const code = event?.error || '';
      if (code === 'not-allowed' || code === 'service-not-allowed') {
        // Микрофон запрещён — задание не состоялось, ничего не сохраняем.
        runningRef.current = false;
        finishedRef.current = true;
        try {
          rec.stop();
        } catch {
          /* ignore */
        }
        setErrorMsg('Нет доступа к микрофону. Разрешите доступ к микрофону (значок в адресной строке) и попробуйте ещё раз.');
        setPhase('error');
      }
      // no-speech / aborted / network — переживаем: onend перезапустит распознавание.
    };

    rec.onend = () => {
      // Chrome сам останавливается после паузы — перезапускаем, пока задание идёт.
      if (!runningRef.current) return;
      window.setTimeout(() => {
        if (!runningRef.current) return;
        try {
          rec.start();
        } catch {
          /* уже запущен — не страшно */
        }
      }, 300);
    };

    try {
      rec.start();
    } catch {
      /* уже запущен — не страшно */
    }
    setPhase('running');
  }, [stopAudio]);

  const startRunRef = useRef(startRun);
  useEffect(() => {
    startRunRef.current = startRun;
  }, [startRun]);

  // Отсчёт «3-2-1» → автостарт. Используется, когда голосовой инструкции ещё нет.
  useEffect(() => {
    if (phase !== 'priming' || countdown <= 0) return undefined;
    const t = window.setTimeout(() => {
      if (countdown <= 1) startRunRef.current();
      else setCountdown((c) => c - 1);
    }, 1000);
    return () => window.clearTimeout(t);
  }, [phase, countdown]);

  // Старт задания: микрофон → голосовая инструкция (или отсчёт «3-2-1»).
  // ТАЙМЕР ПРИ ЭТОМ НЕ ИДЁТ — он стартует только после «Время пошло» (конец аудио).
  //
  // auto=true — автозапуск сразу после загрузки задания (пользователь ещё НЕ
  //   касался экрана /trainer): если браузер блокирует микрофон/звук из-за
  //   отсутствия пользовательской активации — тихо уходим в фолбэк
  //   «Коснитесь экрана», без ошибок на экране.
  // auto=false — запуск по касанию (фолбэк + кнопка Повторить): ошибка
  //   микрофона — уже честная ошибка, показываем экран error.
  const beginSession = useCallback(async (auto = false) => {
    if (!supported || !session) return;
    setErrorMsg('');
    setErrorHint('');

    // Предзапрос микрофона: диалог разрешения не должен звучать поверх инструкции.
    try {
      if (navigator.mediaDevices?.getUserMedia) {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach((t) => t.stop());
      }
    } catch {
      if (auto) {
        // Нет пользовательской активации (страница открыта напрямую) — фолбэк.
        setPhase('intro');
        return;
      }
      setErrorMsg('Нет доступа к микрофону. Разрешите доступ к микрофону в настройках браузера и попробуйте ещё раз.');
      setPhase('error');
      return;
    }

    // Пока запрашивался микрофон, пользователь мог нажать «Пропустить инструкцию» —
    // тогда отсчёт уже идёт и аудио не запускаем.
    if (skippedRef.current) return;

    if (!audioUrl) {
      // Аудио ещё не записано / бэкенд пока не отдаёт ссылку — короткий отсчёт и старт.
      setPhase('priming');
      setCountdown(3);
      return;
    }

    setPhase('priming');
    setCountdown(0);

    const a = new Audio(audioUrl);
    a.preload = 'auto';
    audioRef.current = a;
    let gone = false;
    const go = () => {
      if (gone) return;
      gone = true;
      startRunRef.current();
    };
    const fallback = () => {
      // аудио не загрузилось/не сыграло — уходим в отсчёт, задание всё равно стартует
      if (gone) return;
      gone = true;
      stopAudio();
      setCountdown(3);
    };
    a.onended = go;
    a.onerror = fallback;
    audioSafetyRef.current = window.setTimeout(go, AUDIO_SAFETY_MS);
    try {
      await a.play();
    } catch {
      if (auto) {
        // Автоплей заблокирован (нет пользовательской активации) — фолбэк
        // «Коснитесь экрана»: одно касание вернёт сценарию полный автозапуск.
        stopAudio();
        setPhase('intro');
        return;
      }
      fallback();
    }
  }, [audioUrl, session, stopAudio, supported]);

  // АВТОСТАРТ: задание загрузилось → сразу микрофон + голосовая инструкция,
  // БЕЗ промежуточного экрана «Коснитесь экрана». Клик «Тренироваться» уже дал
  // браузеру пользовательскую активацию (навигация в той же вкладке). Если её
  // нет (/trainer открыт напрямую) — beginSession(true) сам уводит в фолбэк
  // intro; autoStartedRef запрещает повторный автозапуск (иначе цикл),
  // повторный запуск возможен только по касанию — beginSession(false).
  useEffect(() => {
    if (phase !== 'priming' || !session || autoStartedRef.current) return;
    autoStartedRef.current = true;
    void beginSession(true);
  }, [phase, session, beginSession]);

  // «Пропустить инструкцию» — страховка на случай слишком длинной записи.
  const skipAudio = useCallback(() => {
    if (phase !== 'priming') return;
    skippedRef.current = true;
    stopAudio();
    setCountdown(3);
  }, [phase, stopAudio]);

  // Подпись темы: основной источник — config.fluency; если бэкенд отдал
  // категорию/букву в верхнем уровне ответа — берём оттуда (страховка).
  const fluencyFallback = session?.category != null || session?.letter != null
    ? {
        mode: session.letter ? 'phonemic' : (session.category ? 'semantic' : 'pos'),
        letter: session.letter,
        category: session.category,
      }
    : null;
  const fluency = describeFluency(session?.config?.fluency ?? fluencyFallback);
  const secondsLeft = Math.ceil(remainingMs / 1000);
  const count = Number(result?.count ?? 0);
  const countedList = Array.isArray(result?.counted) ? result.counted : [];

  return (
    <div className="flex min-h-screen flex-col bg-cvx-background px-4 py-6">
      <main className="mx-auto flex w-full max-w-xl flex-1 flex-col">
        {/* Шапка: логотип 1:1 с лендингом + «Назад» */}
        <header className="flex items-center justify-between gap-3">
          <a href="/" aria-label="Cortivox.ai на главную" className="flex items-center gap-2.5">
            <span className="flex size-8 items-center justify-center rounded-[10px] bg-cvx-ink text-white">
              <BrainCircuit className="size-4" />
            </span>
            <span className="text-[16px] font-semibold tracking-[-0.04em] text-cvx-ink">
              Cortivox<span className="text-cvx-logo-ai">.ai</span>
            </span>
          </a>
          <button type="button" onClick={() => window.history.back()} className="cvx-back">
            <ArrowLeft className="size-4" /> Назад
          </button>
        </header>

        {/* ЗАГРУЗКА */}
        {phase === 'loading' && (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 py-24 text-cvx-muted-foreground">
            <Loader2 className="size-8 animate-spin text-cvx-primary" />
            <p className="text-sm font-semibold">Загружаем задание…</p>
          </div>
        )}

        {/* СОХРАНЕНИЕ */}
        {phase === 'saving' && (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 py-24 text-cvx-muted-foreground">
            <Loader2 className="size-8 animate-spin text-cvx-primary" />
            <p className="text-sm font-semibold">Сохраняем результат…</p>
          </div>
        )}

        {/* ЭКРАН ЗАДАНИЯ: без кнопок — одно касание в любом месте экрана */}
        {phase === 'intro' && session && (
          <section
            role="button"
            tabIndex={0}
            aria-label="Коснитесь экрана, чтобы начать"
            onClick={() => void beginSession(false)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                void beginSession(false);
              }
            }}
            className={`mt-6 flex flex-1 flex-col ${supported ? 'cursor-pointer select-none' : 'cursor-default'}`}
          >
            <div className="flex flex-1 flex-col items-center justify-center rounded-3xl border border-cvx-border bg-cvx-card p-8 text-center shadow-sm">
              {fluency && (
                <span className="inline-flex items-center rounded-full bg-cvx-accent px-4 py-1.5 text-sm font-bold text-cvx-accent-foreground">
                  {fluency}
                </span>
              )}

              <span className="mt-10 flex size-20 items-center justify-center rounded-full bg-cvx-primary/10">
                <Mic className="size-9 text-cvx-primary" />
              </span>

              <p className="mt-8 text-2xl font-bold tracking-tight text-cvx-foreground">
                Коснитесь экрана
              </p>
              <p className="mt-2 text-sm text-cvx-muted-foreground">
                {audioUrl
                  ? 'Задание начнётся сразу после голосовой инструкции'
                  : session.questionText || 'Задание начнётся после короткого отсчёта'}
              </p>

              {!supported && (
                <div className="mt-8 flex items-start gap-2.5 rounded-2xl border border-cvx-warn/30 bg-cvx-warn-card p-4 text-left">
                  <AlertTriangle className="mt-0.5 size-4 shrink-0 text-cvx-warn" />
                  <p className="text-sm font-semibold text-cvx-warn">
                    Браузер не поддерживает распознавание речи. Откройте тренажёр в Google Chrome или Яндекс.Браузере.
                  </p>
                </div>
              )}
            </div>
          </section>
        )}

        {/* ГОЛОСОВАЯ ИНСТРУКЦИЯ / ОТСЧЁТ — таймер ещё не идёт */}
        {phase === 'priming' && session && (
          <section className="flex flex-1 flex-col items-center justify-center py-8">
            {fluency && (
              <span className="inline-flex items-center rounded-full bg-cvx-accent px-4 py-1.5 text-sm font-bold text-cvx-accent-foreground">
                {fluency}
              </span>
            )}

            <div className="relative mt-8">
              <svg viewBox="0 0 128 128" className="size-44 sm:size-52">
                <circle cx="64" cy="64" r={RING_R} fill="none" strokeWidth="10" className="stroke-cvx-secondary" />
              </svg>
              <span className="absolute inset-0 flex items-center justify-center">
                {countdown > 0 ? (
                  <span className="text-6xl font-bold tabular-nums text-cvx-foreground">{countdown}</span>
                ) : (
                  <Volume2 className="size-12 animate-pulse text-cvx-primary" />
                )}
              </span>
            </div>

            <p className="mt-6 text-sm font-semibold text-cvx-muted-foreground">
              {countdown > 0 ? 'Приготовьтесь…' : 'Голосовая инструкция…'}
            </p>

            {audioUrl && countdown === 0 && (
              <button
                type="button"
                onClick={skipAudio}
                className="mt-10 text-xs font-semibold text-cvx-muted-foreground/70 underline-offset-4 transition-colors hover:text-cvx-foreground hover:underline"
              >
                Пропустить инструкцию
              </button>
            )}
          </section>
        )}

        {/* ЗАДАНИЕ: пациент говорит, таймер идёт */}
        {phase === 'running' && session && (
          <section className="flex flex-1 flex-col items-center pt-8">
            {fluency && (
              <span className="inline-flex items-center rounded-full bg-cvx-accent px-4 py-1.5 text-sm font-bold text-cvx-accent-foreground">
                {fluency}
              </span>
            )}

            <div className="relative mt-6">
              <svg viewBox="0 0 128 128" className="size-44 -rotate-90 sm:size-52">
                <circle cx="64" cy="64" r={RING_R} fill="none" strokeWidth="10" className="stroke-cvx-secondary" />
                <circle
                  cx="64"
                  cy="64"
                  r={RING_R}
                  fill="none"
                  strokeWidth="10"
                  strokeLinecap="round"
                  className="stroke-cvx-primary"
                  strokeDasharray={RING_C}
                  strokeDashoffset={RING_C * (1 - remainingMs / durationMs)}
                />
              </svg>
              <span className="absolute inset-0 flex items-center justify-center text-5xl font-bold tabular-nums text-cvx-foreground">
                {secondsLeft}
              </span>
            </div>

            <p className="mt-5 flex items-center gap-2 text-sm font-semibold text-cvx-foreground">
              <Mic className="size-4 animate-pulse text-cvx-primary" />
              Говорите
            </p>

            <div className="mt-4 min-h-24 w-full rounded-2xl border border-cvx-border bg-cvx-muted p-4" aria-live="polite">
              <p className={`break-words text-lg leading-snug ${liveText ? 'text-cvx-foreground' : 'text-cvx-muted-foreground/60'}`}>
                {liveText || '…'}
              </p>
            </div>
          </section>
        )}

        {/* РЕЗУЛЬТАТ */}
        {phase === 'done' && (
          <section className="mt-6 flex flex-1 flex-col">
            <div className="rounded-3xl border border-cvx-border bg-cvx-card p-6 text-center shadow-sm sm:p-8">
              <span className="mx-auto flex size-14 items-center justify-center rounded-full bg-cvx-accent">
                <CheckCircle2 className="size-7 text-cvx-accent-foreground" />
              </span>
              <h1 className="mt-4 text-xl font-bold text-cvx-foreground">Время вышло — результат сохранён</h1>

              <p className="mt-6 text-6xl font-bold tabular-nums text-cvx-primary">{count}</p>
              <p className="mt-1 text-sm font-semibold text-cvx-muted-foreground">{pluralWords(count)} засчитано</p>

              {countedList.length > 0 && (
                <div className="mt-5 rounded-2xl bg-cvx-muted p-4 text-left">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-cvx-muted-foreground/60">
                    Засчитанные слова
                  </p>
                  <p className="mt-1.5 text-sm leading-relaxed text-cvx-foreground">{countedList.join(', ')}</p>
                </div>
              )}

              <button
                type="button"
                onClick={() => window.history.back()}
                className="mt-6 inline-flex w-full items-center justify-center rounded-full border border-cvx-border bg-cvx-card px-8 py-3.5 text-sm font-bold text-cvx-ink transition-colors hover:bg-cvx-secondary"
              >
                Готово
              </button>
            </div>
          </section>
        )}

        {/* ОШИБКА */}
        {phase === 'error' && (
          <section className="mt-6 flex flex-1 flex-col">
            <div className="rounded-3xl border border-cvx-border bg-cvx-card p-6 text-center shadow-sm sm:p-8">
              <span className="mx-auto flex size-14 items-center justify-center rounded-full bg-cvx-warn">
                <AlertTriangle className="size-7 text-cvx-warn" />
              </span>
              <h1 className="mt-4 text-xl font-bold text-cvx-foreground">Что-то пошло не так</h1>
              <p className="mt-2 text-sm leading-relaxed text-cvx-muted-foreground">{errorMsg}</p>
              {errorHint && <p className="mt-2 text-xs leading-relaxed text-cvx-muted-foreground">{errorHint}</p>}
              <div className="mt-6 flex flex-col gap-2.5 sm:flex-row sm:justify-center">
                <button
                  type="button"
                  onClick={() => void load()}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-cvx-primary px-6 py-3 text-sm font-bold text-cvx-primary-foreground transition-colors hover:bg-cvx-primary/90"
                >
                  <RotateCcw className="size-4" /> Повторить
                </button>
                <button
                  type="button"
                  onClick={() => window.history.back()}
                  className="inline-flex items-center justify-center rounded-full border border-cvx-border bg-cvx-card px-6 py-3 text-sm font-bold text-cvx-ink transition-colors hover:bg-cvx-secondary"
                >
                  Назад
                </button>
              </div>
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
